import React from 'react';
import { LabelAttribute } from '../types/labelAttribute';
import { isAttributeAnswered } from '../pages/labeling/WorkbenchEfficient';

type AttributeFlag = { flagType: string; flagReason: string };

type Props = {
  attributes: LabelAttribute[];
  values: Record<string, any>;
  onChange: (next: Record<string, any>) => void;
  compact?: boolean;
  flags?: Record<string, AttributeFlag>;
  onFlagAttribute?: (attrName: string) => void;
};

function AttributeHeader({
  attr,
  flags,
  onFlagAttribute,
  labelClass,
  helperClass,
}: {
  attr: LabelAttribute;
  flags?: Record<string, AttributeFlag>;
  onFlagAttribute?: (attrName: string) => void;
  labelClass: string;
  helperClass: string;
}) {
  const flag = flags?.[attr.name];

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={labelClass}>
          {attr.displayName}
          {attr.required && ' *'}
        </span>
        {flag && (
          <span className="text-xs bg-yellow-200 text-yellow-800 px-1.5 py-0.5 rounded">
            Flagged
          </span>
        )}
        {onFlagAttribute && (
          <button
            type="button"
            onClick={() => onFlagAttribute(attr.name)}
            className="text-xs px-2 py-0.5 rounded bg-yellow-500 text-white hover:bg-yellow-600"
          >
            Flag
          </button>
        )}
      </div>
      {flag?.flagReason && (
        <span className="block text-xs text-yellow-700 mt-0.5 font-medium">
          Reason: {flag.flagReason}
        </span>
      )}
      {attr.guidanceNote && (
        <span className={`block ${helperClass}`}>{attr.guidanceNote}</span>
      )}
    </>
  );
}

export default function AttributePanel({
  attributes,
  values,
  onChange,
  compact = false,
  flags,
  onFlagAttribute,
}: Props) {
  const setValue = (name: string, value: any) => {
    onChange({ ...values, [name]: value });
  };

  if (attributes.length === 0) {
    return (
      <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-500 italic`}>
        No attributes configured.
      </p>
    );
  }

  const labelClass = compact ? 'text-xs font-medium text-gray-700' : 'text-sm font-medium text-gray-800';
  const helperClass = compact ? 'text-[10px] text-gray-500 mt-0.5' : 'text-xs text-gray-500 mt-1';
  const controlClass = compact
    ? 'mt-1 w-full border border-gray-300 rounded px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500'
    : 'mt-1 w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500';
  const spacingClass = compact ? 'space-y-3' : 'space-y-4';

  const requiredHint = (unanswered: boolean) =>
    unanswered ? <span className="block text-xs text-red-600 mt-1">Required</span> : null;

  const controlClassFor = (unanswered: boolean) =>
    unanswered ? controlClass.replace('border-gray-300', 'border-red-500') : controlClass;

  const flaggedBlockClass = (attrName: string) =>
    flags?.[attrName] ? 'border-l-4 border-yellow-400 bg-yellow-50 rounded pl-2 py-1' : '';

  return (
    <div className={spacingClass}>
      {attributes.map((attr) => {
        const currentValue = values[attr.name];
        const unanswered = attr.required && !isAttributeAnswered(attr, currentValue);

        if (attr.type === 'enum') {
          const selectedOption = (attr.options || []).find((opt) => opt.value === currentValue);
          return (
            <label key={attr.name} className={`block ${flaggedBlockClass(attr.name)}`}>
              <AttributeHeader
                attr={attr}
                flags={flags}
                onFlagAttribute={onFlagAttribute}
                labelClass={labelClass}
                helperClass={helperClass}
              />
              <select
                className={controlClassFor(unanswered)}
                value={currentValue ?? ''}
                onChange={(e) => setValue(attr.name, e.target.value || null)}
              >
                <option value="">Select…</option>
                {(attr.options || []).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label || opt.value}
                  </option>
                ))}
              </select>
              {selectedOption?.description && (
                <span className={`block ${helperClass}`}>{selectedOption.description}</span>
              )}
              {requiredHint(unanswered)}
            </label>
          );
        }

        if (attr.type === 'multi_enum') {
          const selectedValues = Array.isArray(currentValue) ? currentValue : [];
          return (
            <div key={attr.name} className={`block ${flaggedBlockClass(attr.name)}`}>
              <AttributeHeader
                attr={attr}
                flags={flags}
                onFlagAttribute={onFlagAttribute}
                labelClass={labelClass}
                helperClass={helperClass}
              />
              <div
                className={`mt-1 flex flex-wrap gap-1.5 ${compact ? '' : 'gap-2'}${
                  unanswered ? ' border border-red-500 rounded p-1' : ''
                }`}
              >
                {(attr.options || []).map((opt) => {
                  const checked = selectedValues.includes(opt.value);
                  return (
                    <label
                      key={opt.value}
                      className={`inline-flex items-start gap-1.5 border border-gray-200 rounded bg-gray-50 ${
                        compact ? 'px-1.5 py-1 text-xs' : 'px-2 py-1.5 text-sm'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const cur = Array.isArray(currentValue) ? [...currentValue] : [];
                          const next = e.target.checked
                            ? [...cur, opt.value]
                            : cur.filter((v) => v !== opt.value);
                          setValue(attr.name, next);
                        }}
                        className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>
                        <span className="text-gray-800">{opt.label || opt.value}</span>
                        {opt.description && (
                          <span className={`block ${helperClass}`}>{opt.description}</span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
              {requiredHint(unanswered)}
            </div>
          );
        }

        if (attr.type === 'boolean') {
          return (
            <div key={attr.name} className={`block ${flaggedBlockClass(attr.name)}`}>
              <div className="mb-1">
                <AttributeHeader
                  attr={attr}
                  flags={flags}
                  onFlagAttribute={onFlagAttribute}
                  labelClass={labelClass}
                  helperClass={helperClass}
                />
              </div>
              <label className={`flex items-start gap-2${unanswered ? ' border border-red-500 rounded p-1' : ''}`}>
                <input
                  type="checkbox"
                  checked={!!currentValue}
                  onChange={(e) => setValue(attr.name, e.target.checked)}
                  className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-600">Checked</span>
              </label>
              {requiredHint(unanswered)}
            </div>
          );
        }

        if (attr.type === 'number') {
          return (
            <label key={attr.name} className={`block ${flaggedBlockClass(attr.name)}`}>
              <AttributeHeader
                attr={attr}
                flags={flags}
                onFlagAttribute={onFlagAttribute}
                labelClass={labelClass}
                helperClass={helperClass}
              />
              <input
                type="number"
                className={controlClassFor(unanswered)}
                value={currentValue ?? ''}
                onChange={(e) =>
                  setValue(attr.name, e.target.value === '' ? null : Number(e.target.value))
                }
              />
              {requiredHint(unanswered)}
            </label>
          );
        }

        return (
          <label key={attr.name} className={`block ${flaggedBlockClass(attr.name)}`}>
            <AttributeHeader
              attr={attr}
              flags={flags}
              onFlagAttribute={onFlagAttribute}
              labelClass={labelClass}
              helperClass={helperClass}
            />
            <input
              type="text"
              className={controlClassFor(unanswered)}
              value={currentValue ?? ''}
              onChange={(e) => setValue(attr.name, e.target.value || null)}
            />
            {requiredHint(unanswered)}
          </label>
        );
      })}
    </div>
  );
}
